// src/pages/LoginPage.jsx

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser, fetchCurrentUser } from "../userSlice";
import { login } from "../services/authService";
import { acceptInvitation } from "../services/invitationService";
import { createJoinRequest } from "../services/joinRequestService";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Link,
  Alert
} from "@mui/material";
import { useSnackbar } from "notistack";
import AuthHeader from "../components/AuthHeader";

function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [inviteToken, setInviteToken] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("inviteToken");
    const e = params.get("inviteEmail");
    const orgIdParam = params.get("orgId");
    const orgNameParam = params.get("orgName");
    
    if (t) setInviteToken(t);
    if (e) setEmail(e);
    if (orgIdParam) setOrgId(orgIdParam);
    if (orgNameParam) setOrgName(decodeURIComponent(orgNameParam));
  }, [location.search]);

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const data = await login(email, password, rememberMe);
      dispatch(setUser(data.user));
      
      // Fetch fresh user data to get organization details
      const updatedUserData = await dispatch(fetchCurrentUser()).unwrap();

      enqueueSnackbar(data.message || "Logged in", { variant: "success" });

      if (inviteToken) {
        try {
          await acceptInvitation(inviteToken);
          enqueueSnackbar("Invitation accepted", { variant: "success" });
          // Users joining via invitation skip onboarding
          navigate("/rooms");
          return;
        } catch (err) {
          enqueueSnackbar("Failed to accept invitation", { variant: "error" });
        }
      }

      // Handle organization join request if present
      const user = updatedUserData || data.user;
      if (orgId && !user.organization) {
        // User came from organization invitation link and has no organization
        try {
          await createJoinRequest(orgId, `Joined via invitation link for ${orgName}`);
          enqueueSnackbar("Join request submitted successfully! Please wait for admin approval.", { variant: "success" });
          navigate("/join-pending", { state: { organizationName: orgName } });
          return;
        } catch (err) {
          console.error("Failed to create join request:", err);
          enqueueSnackbar("Login successful, but failed to submit join request. Please try joining manually.", { variant: "warning" });
        }
      }

      // Determine redirect destination based on onboarding status
      if (user && user.organization && user.organization.onboarding && !user.organization.onboarding.isCompleted) {
        // User needs to complete onboarding
        const currentStep = user.organization.onboarding.currentStep || 'welcome';
        navigate(`/onboarding/${currentStep}`);
      } else if (user && user.organization) {
        // User has completed onboarding or doesn't need it
        navigate("/rooms");
      } else {
        // User has no organization
        navigate("/organization-choice");
      }
    } catch (err) {
      if (err.response && err.response.data?.error) {
        enqueueSnackbar(err.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("Login failed", { variant: "error" });
      }
    }
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }} elevation={2}>
          <Typography variant="h5" gutterBottom>
            Login
          </Typography>
        
        {orgName && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You're logging in to join <strong>{orgName}</strong>. 
            After login, your join request will be submitted for admin approval.
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
        >
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            InputProps={{
              readOnly: !!inviteToken
            }}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
            }
            label="Remember me"
          />

          <Button variant="contained" type="submit">
            Login
          </Button>

          {/* Link to forgot password page */}
          <Box>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Forgot password?
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
    </>
  );
}

export default LoginPage;